import { useEffect, useState, useRef } from "react";
import moment from "moment";

//components
import Chat from "./components/chat/Chat";
import ProductModal from "./components/modals/ProductModal";
import OrderConfirmationModal from "./components/modals/OrderConfirmationModal";
import { motion, AnimatePresence } from "framer-motion";

//contexts
import { UserActivityProvider, useUserActivity } from "./contexts/UserActivityContext"; 

//icons
import AiChatAvatar from "./assets/ai-chat-avatar.svg";

//redux
import { useSelector } from "react-redux";

//tools
import сс from "classcat";
import {
	getAiChatButtonActionIntents,
	getAiChatNavigationIntents,
	handleAiChatAction,
} from "./tools/aiChatActions";

//styles
import styles from "./App.module.css";

function AppContent() {
	const renderChat = useSelector((state) => state.chat.render_chat);
	const anyModalOpen = useSelector((state) => state.chat_modals.any_modal_open);
	const audioAutoPlay = useSelector((state) => state.chat.audio_auto_play);
	const attention = useSelector((state) => state.chat.attention);
	const { updateActivity } = useUserActivity();

	const [messages, setMessages] = useState([]);
	const [menuGuid, setMenuGuid] = useState(null);
	const [tableGuid, setTableGuid] = useState(null);
	const [isConnected, setIsConnected] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	const audioPlayedIdsRef = useRef([]);
	const lastActivitySentRef = useRef(moment().subtract(10, 'seconds'));
	const historyRequestPromiseRef = useRef(null);

	const handleBackdropClick = () => {
		if (anyModalOpen) return window.store.emit("close_all_modals");
		window.store.emit("close_chat");
	};

	const sendUserActivity = () => {
		if (!isConnected) return;
		
		const now = moment();
		const timeSinceLastActivity = now.diff(lastActivitySentRef.current, 'milliseconds');
		
		if (timeSinceLastActivity >= 5000) {
			lastActivitySentRef.current = now;
			updateActivity(now);
			__cng_websocket_manager?.send_user_activity();
		}
	};

	const loadHistory = async () => {
		if (historyRequestPromiseRef.current) {
			return historyRequestPromiseRef.current;
		}

		setIsLoading(true);

		const requestPromise = (async () => {
			try {
				const historyResult = await __cng_websocket_manager?.fetch_history();
				if (historyResult?.success && historyResult?.messages) {
					const lastMessage = historyResult.messages?.at(-1);
					setMessages(historyResult.messages);

					if(typeof historyResult?.data?.basket_count === 'number'){
						window.store.emit("set_products_count", historyResult.data.basket_count);
					}

					if (lastMessage) {
						historyResult.messages.at(-1).scrollDown = true;
					}

					if(historyResult.data?.mguid){
						setMenuGuid(historyResult.data.mguid);
					}

					if(historyResult.data?.tguid){
						setTableGuid(historyResult.data.tguid);
					}
				} else if(historyResult?.redirect_to_scan_qr){
					__cng_common.redirect_to_scan_page();
				}
			} catch (error) {
				console.error("Failed to load chat history:", error);
			} finally {
				setIsLoading(false);
			}
		})();

		historyRequestPromiseRef.current = requestPromise.finally(() => {
			if (historyRequestPromiseRef.current === requestPromise) {
				historyRequestPromiseRef.current = null;
			}
		});

		return historyRequestPromiseRef.current;
	};

	const handleConnected = () => setIsConnected(true);

	const handleDisconnected = () => setIsConnected(false);

	const handleMessage = (data) => {
		const dataJson = typeof data === "string" ? JSON.parse(data) : data;

		const isBot = dataJson?.type === "bot";
		const isUser = dataJson?.type === "user";
		const toCurrentUser = dataJson?.user_guid === window.__cng_common.user_guid;
		if (toCurrentUser) __cng_common.user_chat_name = dataJson.author_name;
		if (
			isBot &&
			toCurrentUser &&
			audioAutoPlay &&
			!audioPlayedIdsRef.current.includes(dataJson.id)
		) {
			dataJson.autoplay = true;
		}

		if (isUser && toCurrentUser) {
			dataJson.showBotTyping = true;
		}

		if(dataJson.basket_count){
			window.store.emit("set_products_count", dataJson.basket_count);
		}
		
		if(Array.isArray(dataJson.intent)){
			const buttonActionIntents = getAiChatButtonActionIntents(dataJson.intent);
			const navigationIntents = getAiChatNavigationIntents(dataJson.intent);
			const runNavigationAction = (action) => {
				handleAiChatAction(action, { anyModalOpen });
				return anyModalOpen;
			};

			if(buttonActionIntents.length <= 1) {
				if(dataJson.intent.includes('finalize_order')){
					handleAiChatAction('finalize_order', { anyModalOpen });
					if (anyModalOpen) return;
					
				} else if(navigationIntents.length <= 1) {
					if(dataJson.intent.includes('leave_a_tip')){
						if (runNavigationAction('leave_a_tip')) return;
						
					} else if(dataJson.intent.includes('call_the_waiter')){
						handleAiChatAction('call_the_waiter');
						
					} else if(dataJson.intent.includes('view_menu')){
						if (runNavigationAction('view_menu')) return;
						
					} else if(dataJson.intent.includes('view_loyalty_program')){
						if (runNavigationAction('view_loyalty_program')) return;
					}
				}
			} else if(dataJson.intent.includes('call_the_waiter')){
				handleAiChatAction('call_the_waiter');
			}

		}

		if(!renderChat) {
			window.store.patch('chat', { attention: true });
		}

		setMessages((prev) => [...prev, dataJson]);
	};

	const handleAlert = (data) => {
		const dataJson = typeof data === "string" ? JSON.parse(data) : data;

		if (dataJson?.event_type === "order_confirmed") {
			if (anyModalOpen) return window.store.emit("close_all_modals");
			window.store.emit("close_chat");
		}
	};

	const handleRouting = (data) => {
		const dataJson = typeof data === "string" ? JSON.parse(data) : data;

		if (dataJson?.route === "scan_qr") {
			__cng_common.redirect_to_scan_page();
		}
	};

	const handleError = (data) => {
		const errorMessage = {
			id: Date.now(),
			author: "Carlos",
			avatar: AiChatAvatar,
			text: `Error: ${JSON.stringify(data)}`,
			audio: null,
			replyTo: null,
			created_at: new Date().toISOString(),
		};
		setMessages((prev) => [...prev, errorMessage]);
	};

	useEffect(() => {
		if (__cng_websocket_manager?.is_connected) {
			setIsConnected(true);
		}

		__cng_websocket_manager?.on("connected", handleConnected);
		__cng_websocket_manager?.on("disconnected", handleDisconnected);
		__cng_websocket_manager?.on("error", handleError);
		__cng_websocket_manager?.on("message", handleMessage);
		__cng_websocket_manager?.on("alert", handleAlert);
		__cng_websocket_manager?.on("routing", handleRouting);

		return () => {
			__cng_websocket_manager?.off("connected", handleConnected);
			__cng_websocket_manager?.off("disconnected", handleDisconnected);
			__cng_websocket_manager?.off("error", handleError);
			__cng_websocket_manager?.off("message", handleMessage);
			__cng_websocket_manager?.off("alert", handleAlert);
			__cng_websocket_manager?.off("routing", handleRouting);
		};
	}, []);

	useEffect(() => {
		if (renderChat) {
			document.body.style.overflow = "hidden";
			window.scrollTo({ top: 0, behavior: "smooth" });
			loadHistory();

		} else {
			document.body.style.overflow = "auto";
		}

		window.store.patch('chat', { attention: false });
	}, [renderChat]);

	useEffect(() => {
		const attentionBlock = document.getElementById("cng_ai_chat_avatar_attention_block");
		if (attention && attentionBlock) {
			attentionBlock.classList.add("shown");
		} else if (attentionBlock) {
			attentionBlock.classList.remove("shown");
		}
	}, [attention]);

	if (!renderChat && !anyModalOpen) return null;

	const backdropClass = сс([
		styles.backdrop,
		{
			[styles.hidden]: !renderChat && !anyModalOpen,
			[styles.level1]: anyModalOpen,
			[styles.visible]: renderChat || anyModalOpen,
		},
	]);

	return (
		<>
			<AnimatePresence>
				{(renderChat || anyModalOpen) && (
					<motion.div
						className={backdropClass}
						onClick={handleBackdropClick}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.25 }}
					/>
				)}
			</AnimatePresence>
			<AnimatePresence>
				{renderChat && (
					<motion.div
						initial={{ opacity: 0, scale: 0.95 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.95 }}
						transition={{ duration: 0.25 }}>
						<Chat
							messages={messages}
							isConnected={isConnected}
							isLoading={isLoading}
							audioPlayedIdsRef={audioPlayedIdsRef}
							menuGuid={menuGuid}
							tableGuid={tableGuid}
							onUserActivity={sendUserActivity}
						/>
					</motion.div>
				)}
			</AnimatePresence>
			<ProductModal />
			<OrderConfirmationModal />
		</>
	);
}

function App() {
	return (
		<UserActivityProvider>
			<AppContent />
		</UserActivityProvider>
	);
}

export default App;
